import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Binary, CheckCircle2, ChevronLeft, CreditCard, IndianRupee, Loader2, Lock } from 'lucide-react';
import styles from './PrepaidMembershipForm.module.css';

// First 4 digits of every card number = the card level
const CARD_TYPES = [
    { label: 'Bronze', code: '1200' }, { label: 'Silver', code: '2000' },
    { label: 'Gold', code: '3000' }, { label: 'Premium', code: '4000' },
    { label: 'Diamond', code: '5000' }, { label: 'VIP', code: '8000' },
];

/** Last 2 digits of the year in India time (the server uses the same) */
const yearYY = () => new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', year: 'numeric' }).format(new Date()).slice(-2);

/**
 * Card number (16 digits): level (4) + year (2) + store code (6) + sequence (4)
 *   4000 26 260003 0001  ->  4000 2626 0003 0001
 * The store code comes from the branch (server); the sequence restarts every year.
 */
export default function PrepaidMembershipForm({ initialData, existingTiers = [], storeCode = '', cardYear = '', onCancel, onSave, isSaving }) {
    const [step, setStep] = useState('FORM');
    const [generatedList, setGeneratedList] = useState([]);
    const [error, setError] = useState('');

    const isEditMode = Boolean(initialData);

    // card levels already used in this branch cannot be created again
    const usedCardCodes = useMemo(() => existingTiers.map((tier) => tier.cardCode), [existingTiers]);

    const [formData, setFormData] = useState({
        name: '',
        cardObj: CARD_TYPES[3],
        validity: '12',
        validityType: 'Mos',
        clientPays: '',
        prepaidCredit: '',
        batchSize: '50',
    });

    // new tier: start on the first level not used yet
    useEffect(() => {
        if (!isEditMode && usedCardCodes.length > 0) {
            setFormData((prev) => {
                if (usedCardCodes.includes(prev.cardObj.code)) {
                    const firstAvailable = CARD_TYPES.find((c) => !usedCardCodes.includes(c.code));
                    return firstAvailable ? { ...prev, cardObj: firstAvailable } : prev;
                }
                return prev;
            });
        }
    }, [usedCardCodes, isEditMode]);

    // edit mode: fill the tier's values
    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name,
                cardObj: CARD_TYPES.find((c) => c.code === initialData.cardCode) || { label: 'Card', code: initialData.cardCode || '0000' },
                validity: String(initialData.validity || '12'),
                validityType: initialData.validityType === 'Yrs' ? 'Yrs' : 'Mos',
                clientPays: initialData.clientPays,
                prepaidCredit: initialData.prepaidCredit,
                batchSize: '50',
            });
        }
    }, [initialData]);

    const yy = cardYear || yearYY();
    const code = String(storeCode || '');
    // 16 digits shown as 4 groups: level | YY + first 2 of store code | last 4 of store code | sequence
    const pattern = `${formData.cardObj.code}${yy}${code || '······'}XXXX`;
    const groups = pattern.match(/.{1,4}/g) || [];
    const allLevelsUsed = !isEditMode && CARD_TYPES.every((c) => usedCardCodes.includes(c.code));

    const handleInput = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

    const executeGeneration = async () => {
        if (!formData.name || !formData.clientPays || !formData.prepaidCredit || !formData.batchSize) {
            setError('Fill all required fields.');
            return;
        }
        if (Number(formData.batchSize) < 1 || Number(formData.batchSize) > 500) {
            setError('Generate between 1 and 500 cards at a time.');
            return;
        }
        setError('');
        const result = await onSave(formData);
        if (result && result.success) {
            setGeneratedList(result.cards || []);
            setStep('GENERATED');
        } else if (result?.message) {
            setError(result.message);
        }
    };

    return (
        <div className={styles.workspace}>
            <header className={styles.topBar}>
                <div className={styles.topLeft}>
                    <button type="button" className={styles.backBtn} onClick={onCancel} aria-label="Back"><ChevronLeft size={17} /></button>
                    <div className={styles.pageTitles}>
                        <span className={styles.breadcrumb}>Loyalty / Prepaid Memberships</span>
                        <h2 className={styles.pageTitle}>{step === 'FORM' ? (isEditMode ? 'Update Tier' : 'Create Prepaid Cards') : 'Cards Ready'}</h2>
                    </div>
                </div>
                <div className={styles.topRight}>
                    <button type="button" className={styles.btnDraft} onClick={onCancel} disabled={isSaving}>{step === 'GENERATED' ? 'Close' : 'Cancel'}</button>
                </div>
            </header>

            <div className={styles.mainSplit}>
                <section className={styles.leftPanel}>
                    {step === 'FORM' ? (
                        <>
                            <div className={styles.formBody}>
                                {isEditMode && (
                                    <div style={{ background: 'var(--h-color-primary-50, #f5f6ff)', padding: '10px', borderRadius: '6px', fontSize: '11px', color: 'var(--h-color-primary-700, #471ea7)', fontWeight: 600 }}>
                                        <AlertCircle size={14} style={{ display: 'inline', marginBottom: '-3px', marginRight: '4px' }} />
                                        This tier is locked. You can update the price or generate more cards.
                                    </div>
                                )}

                                <div className={styles.formSectionLabel}><CreditCard size={13} /> Prepaid Card Identity</div>
                                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                                    <label htmlFor="pp-name">Card Name (e.g. VIP Prepaid)</label>
                                    <input id="pp-name" type="text" className={styles.sleekInput} value={formData.name} onChange={(e) => handleInput('name', e.target.value)} disabled={isEditMode} />
                                </div>
                                <div className={styles.grid2}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="pp-level">Card level (first 4 digits)</label>
                                        <select id="pp-level" className={styles.sleekSelect} value={formData.cardObj.code} onChange={(e) => handleInput('cardObj', CARD_TYPES.find((c) => c.code === e.target.value))} disabled={isEditMode}>
                                            {CARD_TYPES.map((t) => {
                                                const isUsed = !isEditMode && usedCardCodes.includes(t.code);
                                                return (
                                                    <option key={t.code} value={t.code} disabled={isUsed}>
                                                        {t.label} ({t.code}){isUsed ? ' - already created' : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="pp-validity">Validity</label>
                                        <div className={styles.inputGroupSplit}>
                                            <input id="pp-validity" type="number" min="1" className={styles.splitLeft} value={formData.validity} onChange={(e) => handleInput('validity', e.target.value)} />
                                            <select className={styles.splitRight} value={formData.validityType} onChange={(e) => handleInput('validityType', e.target.value)} aria-label="Validity unit">
                                                <option value="Mos">Mos</option><option value="Yrs">Yrs</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.formSectionLabel} style={{ marginTop: '10px' }}><IndianRupee size={13} /> Financial Model</div>
                                <div className={styles.grid2}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="pp-pays">Client Pays (₹)</label>
                                        <div className={styles.inputWithIcon}>
                                            <IndianRupee size={14} /><input id="pp-pays" type="number" min="0" className={styles.sleekInput} value={formData.clientPays} onChange={(e) => handleInput('clientPays', e.target.value)} />
                                        </div>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="pp-credit">Prepaid Credit Given (₹)</label>
                                        <div className={styles.inputWithIcon}>
                                            <IndianRupee size={14} /><input id="pp-credit" type="number" min="0" className={styles.sleekInput} value={formData.prepaidCredit} onChange={(e) => handleInput('prepaidCredit', e.target.value)} />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.formSectionLabel} style={{ marginTop: '10px' }}><Binary size={13} /> Inventory Setup</div>
                                <div className={styles.grid2}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="pp-store">Store code</label>
                                        <div className={styles.inputWithIcon} title="Your branch's Hirsta code. It is set by Hirsta and cannot be changed.">
                                            <Lock size={13} />
                                            <input id="pp-store" type="text" className={styles.sleekInput} value={code || 'Loading...'} readOnly disabled />
                                        </div>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="pp-batch">Cards to Generate (1-500)</label>
                                        <input id="pp-batch" type="number" min="1" max="500" className={styles.sleekInput} value={formData.batchSize} onChange={(e) => handleInput('batchSize', e.target.value)} />
                                    </div>
                                </div>
                                <p style={{ margin: '2px 0 0', fontSize: 11, fontWeight: 600, color: 'var(--h-color-neutral-500, #797980)', lineHeight: 1.5 }}>
                                    Card number: level {formData.cardObj.code} + year {yy} + store code {code || '—'} + a 4-digit number that starts again at 0001 every year.
                                </p>

                                {error && <div className={styles.inlineError}><AlertCircle size={14} /> {error}</div>}
                            </div>
                            <div className={styles.panelFooter}>
                                <button type="button" className={styles.primaryBtn} onClick={executeGeneration} disabled={isSaving || allLevelsUsed || !code}>
                                    {isSaving ? <><Loader2 className="animate-spin" size={15} /> Making cards...</> : 'Generate Inventory'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className={styles.formBody}>
                            <h3 style={{ color: '#007a49', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CheckCircle2 size={20} /> {generatedList.length} cards ready
                            </h3>
                            <p style={{ margin: 0, fontSize: 11.5, fontWeight: 600, color: 'var(--h-color-neutral-500, #797980)' }}>
                                Level {formData.cardObj.code} · year {yy} · store {code} · number
                            </p>
                            <div className={styles.seriesListScroll}>
                                {generatedList.map((num) => <div key={num} className={styles.seriesItem}>{num}</div>)}
                            </div>
                            <button type="button" className={styles.primaryBtn} onClick={onCancel} style={{ marginTop: 'auto' }}>Done & Back to List</button>
                        </div>
                    )}
                </section>

                <section className={styles.rightPanel}>
                    {/* card preview */}
                    <div className={styles.glassCard}>
                        <div className={styles.cardHeader}>
                            <span className={styles.brandName}>Hirsta</span>
                            <span className={styles.tierBadge}>{formData.cardObj.label}</span>
                        </div>
                        <div className={styles.cardNumber}>
                            {groups.map((g, i) => <span key={i}>{g}</span>)}
                        </div>
                        <div className={styles.cardFooter}>
                            <div className={styles.cardHolder}>
                                <span className={styles.label}>Membership</span>
                                <span className={styles.value}>{formData.name || 'PREPAID CARD'}</span>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}